import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllEmployeeLeaveData } from "../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { fetchAllHolidaysByMonth } from "../../../core/data/redux/holiday/holidayAction";
import { createSalaryTransaction, fetchSalaryTransactions } from "../../../core/data/redux/payroll/payrollSalary/salaryTransaction/salaryTransactionActions";
import { Drawer, message } from "antd";
import { createPayslip, fetchPayslipsByEmployeeId } from "../../../core/data/redux/payroll/payrollSalary/payslip/payslipActions";
import PayslipModal from "./payslipModal";
import html2pdf from 'html2pdf.js';
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { formatCurrency } from "../../../core/utils/configFormatters";
import { fetchAllPaySlipReport } from "../../../core/data/redux/reports/paySlip_report/payslipAction";
import { decryptData } from "../../../core/utils/encryptUtils";


const monthNameToNumber: { [key: string]: number } = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
};

const EmployeeSalary = () => {
    const dispatch = useDispatch<AppDispatch>();
    const currentDate = new Date();
    const defaultYear = currentDate.getFullYear().toString();
    const defaultMonth = currentDate.toLocaleString('default', { month: 'long' });
    const [selectedYear, setSelectedYear] = useState<string>(defaultYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonth);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isPaySlipModalVisible, setIsPaySlipModalVisible] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('Processed');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const [payslipData, setPayslipData] = useState<any>([]);
    const [tableKey, setTableKey] = useState(0);
    const [tableKeyTransaction, setTableKeyTransaction] = useState(0);
    const { salryCalculation } = useSelector((state: any) => state.payroll.salaryStructure);
    const { salaryTransactions } = useSelector((state: any) => state.payroll.salaryTransaction);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const { user } = useSelector((state: any) => state.auth);

    const { holidaysByMonths } = useSelector((state: any) => state.holiday);
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 14 }, (_, index) => currentYear - index);

    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd =
        permissions.includes("Add") || permissions.includes("AllowAll");
    const canView =
        permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit =
        permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete =
        permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");

    useEffect(() => {
        dispatch(fetchAllPaySlipReport()).then((res) => {
            setPayslipData(res.payload.data);
        });
    }, []);

    const handleYearSelect = (year: string) => {
        setSelectedYear(year);
        setSelectedMonth('Select Month');
    };

    const handleMonthSelect = (month: string) => {
        setSelectedMonth(month);
    };

    useEffect(() => {
        if (selectedRowData?.employee_id) {
            dispatch(fetchPayslipsByEmployeeId({
                employee_id: selectedRowData.employee_id,
                month_name: selectedRowData.month_name,
                year: selectedRowData.year
            }));
        }
    }, [selectedRowData, dispatch]);


    useEffect(() => {
        if (selectedYear !== 'Select Year' && selectedMonth !== 'Select Month') {
            const monthNumber = monthNameToNumber[selectedMonth];
            const year = parseInt(selectedYear, 10);

            dispatch(fetchAllEmployeeLeaveData({ month: monthNumber, year }));
            dispatch(fetchSalaryTransactions({ year, month: monthNumber }));
        }

        if (!holidaysByMonths?.length) {
            dispatch(fetchAllHolidaysByMonth());
        }
    }, [dispatch, selectedYear, selectedMonth]);

    // ---------------------------------------------------
    // AI → PROCESS SALARY (AUTO TRIGGER)
    // ---------------------------------------------------
    useEffect(() => {
        const handler = async (event: any) => {
            const { year, month } = event.detail || {};

            if (!year || !month) {
                message.error("Year and Month are required to process salary");
                return;
            }

            setSelectedYear(String(year));
            setSelectedMonth(month);

            setTimeout(async () => {
                const fakeEvent = {
                    preventDefault: () => { }
                } as React.FormEvent;

                await handleProcessSalary(fakeEvent);
            }, 500);
        };

        window.addEventListener("ai-process-salary", handler);

        return () => {
            window.removeEventListener("ai-process-salary", handler);
        };
    }, [salryCalculation, selectedYear, selectedMonth]);


    const showModal = () => {
        if (selectedYear === 'Select Year' || selectedMonth === 'Select Month') {
            message.warning('Please select both the Year and the Month to proceed.');
        } else {
            setIsModalVisible(true);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleProcessSalary = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedYear === 'Select Year' || selectedMonth === 'Select Month') {
            message.warning('Please select both the Year and the Month to proceed.');
            return;
        }
        try {
            for (const employee of salryCalculation) {
                if (employee.salary_structure_id !== null) {
                    const data = {
                        employee_id: employee.EmployeeID,
                        salary_structure_id: employee.salary_structure_id,
                        salary: employee.net_salary,
                        payment_date: new Date().toISOString(),
                        year: parseInt(selectedYear),
                        month_name: selectedMonth,
                        status: 'Processed',
                        CreatedBy: user.UserId,
                        overtime_pay_id: employee.overtime_pay_id,
                        leaves_taken: employee.leave_days,
                        balance_leave: employee.available_leave_balance,
                        total_attendance_count: employee.present_full,
                        total_earnings: employee.total_earnings,
                        total_deductions: employee.total_deduction_including_statutory,
                        net_salary: employee.net_salary,
                        daily_earnings: employee.daily_earnings,
                        leave_deduction: employee.leave_deduction,
                        overtime_hours: employee.overtime_hours,
                        overtime_pay: employee.overtime_pay,
                        total_working_days: employee.total_working_days,
                        holiday_count: employee.holiday_count,
                        weekend_count: employee.weekend_count,
                        earnings_json: employee.earnings,
                        deductions_json: employee.prorated_deduction_details,
                    };
                    await dispatch(createSalaryTransaction(data));
                }
            }
            message.success('Salaries processed successfully for all employees with valid salary structures!');
            setIsModalVisible(false);

        } catch (error) {
            message.error('An error occurred while processing salaries.');
        }
    };

    const handleGeneratePayslip = async (record: any) => {
        setSelectedRowData(record);
        try {
            const result = await dispatch(createPayslip(record));
            if (result?.payload?.message === "Payslip already exists for this employee in the specified month.") {
                message.warning('Payslip already exists for this employee in the specified month.');
            } else {
                message.warning(result?.payload?.message);
                setIsPaySlipModalVisible(true);
            }
        } catch (error) {
            message.error('Error generating payslip.');
        }
    };


    const downloadPayslipAsPDF = (record: any) => {
        setSelectedRowData(record)
        setLoadingDownload(true);
        const element = document.getElementById('payslip-content');
        if (element) {
            const opt = {
                margin: 1,
                filename: 'payslip.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            };
            html2pdf().from(element).set(opt).save();
        }
    };

    const printPayslip = (record: any) => {
        setSelectedRowData(record);
        const printContent = document.getElementById('payslip-content');

        if (printContent) {
            const printWindow = window.open('', '', 'height=800,width=1200');

            if (printWindow) {
                let styleSheets = '';
                // Loop through all styleSheets and collect the ones with 'outerHTML'
                Array.from(document.styleSheets).forEach((sheet) => {
                    if (sheet instanceof CSSStyleSheet && sheet.ownerNode instanceof HTMLElement) {
                        styleSheets += sheet.ownerNode.outerHTML;
                    }
                });

                printWindow.document.write('<html><head><title>Payslip</title>');
                printWindow.document.write('<style>/* You can add custom styles here for the print view */');
                printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
                printWindow.document.write('.modal-content { border: 1px solid #ccc; padding: 20px; }');
                printWindow.document.write('.text-info { color: #17a2b8; }');
                printWindow.document.write('.text-success { color: #28a745; }');
                printWindow.document.write('.text-danger { color: #dc3545; }');
                printWindow.document.write('.text-muted { color: #6c757d; }');

                // Ensure we maintain the original layout (e.g., flexbox or grid)
                printWindow.document.write('.row { display: flex; flex-wrap: wrap; }');
                printWindow.document.write('.col-md-6 { width: 50%; padding: 15px; }');
                printWindow.document.write('.text-end { text-align: right; }');
                printWindow.document.write('.modal-header { background-color: #007bff; padding: 10px; color: white; }');
                printWindow.document.write('.modal-title { font-size: 1.5em; }');
                printWindow.document.write('.fw-medium { font-weight: 500; }');
                printWindow.document.write('.list-group-item { padding: 10px; }');
                printWindow.document.write('.list-group-item.bg-light { background-color: #f8f9fa; }');
                printWindow.document.write('.footer { background-color: #f8f9fa; padding: 10px; text-align: center; margin-top: 20px; }');
                printWindow.document.write('.footer p { margin: 0; }');
                printWindow.document.write(styleSheets); // Inject existing styles from the current document
                printWindow.document.write('</style>');
                printWindow.document.write('</head><body>');

                // Copy the HTML content exactly as it is
                printWindow.document.write(printContent.innerHTML);

                printWindow.document.write('</body></html>');
                printWindow.document.close();
                printWindow.print();
            } else {
                console.error('Failed to open print window.');
            }
        } else {
            console.error('Payslip content not found.');
        }
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = salaryTransactions ? salaryTransactions : [];

    const exportEmployeeSalary = data.map((item: any) => ({
        "Name": item.EmployeeName,
        "Year": item.year,
        "Month": item.month_name,
        "Leaves": item.leaves_taken,
        "Balance Leave": item.balance_leave,
        "Total Attendance": item.total_attendance_count,
        "Daily Earnings": item.daily_earnings,
        "Leave Deduction": item.leave_deduction,
        "Overtime Hours": item.overtime_hours,
        "Overtime Pay": item.overtime_pay,
        "Total Working Days": item.total_working_days,
        "Holiday Count": item.holiday_count,
        "Weekend Count": item.weekend_count,
        "Earnings": item.total_earnings,
        "Deductions": item.total_deductions,
        "Net Salary": item.net_salary,
    }));


    const filteredData = useMemo(() => {
        return filterData(data || [], { status: selectedStatus }, "CreatedAt", "CreatedAt", selectedSort);
    }, [data, selectedSort, selectedStatus]);


    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [salryCalculation]);

    useEffect(() => {
        setTableKeyTransaction(prevKey => prevKey + 1);
    }, [selectedSort, salaryTransactions, selectedStatus]);

    const config = useAppConfig();
    if (!config) return null;

    const payslipMap = new Map();
    if (Array.isArray(payslipData)) {
        payslipData.forEach((p) => {
            payslipMap.set(p.transaction_id, p);
        });
    } else if (payslipData?.transaction_id) {
        payslipMap.set(payslipData.transaction_id, payslipData);
    }

    const columns = [
        {
            title: "Emp ID",
            dataIndex: "employee_number",
            sorter: (a: any, b: any) => a.employee_number.length - b.employee_number.length,
        },
        {
            title: "Name",
            dataIndex: "employee_name",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center file-name-icon">
                    <div className="avatar avatar-md">
                        <img
                            src={record.PictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                            alt="img"
                            className="img-fluid rounded-circle"
                        />
                    </div>
                    <div className="ms-2">
                        <h6 className="fw-medium">
                            <Link to="#">{record.employee_name}</Link>
                        </h6>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.employee_name.length - b.employee_name.length,
        },
        {
            title: "Present Days",
            dataIndex: "present_full",
            sorter: (a: any, b: any) => a.present_full - b.present_full,
        },
        {
            title: "Pay Days",
            dataIndex: "pay_days",
            sorter: (a: any, b: any) => a.pay_days - b.pay_days,
        },
        {
            title: "Leaves Taken",
            dataIndex: "leave_days",
            sorter: (a: any, b: any) => a.leave_days - b.leave_days,
        },
        {
            title: "Paid Leave",
            dataIndex: "paid_leave_days",
            sorter: (a: any, b: any) => a.paid_leave_days - b.paid_leave_days,
        },
        {
            title: "Unpaid Leave",
            dataIndex: "unpaid_leave_days",
            sorter: (a: any, b: any) => a.unpaid_leave_days - b.unpaid_leave_days,
        },
        {
            title: "Weekly Off",
            dataIndex: "weekoffs",
            sorter: (a: any, b: any) => a.weekoffs - b.weekoffs,
        },
        // {
        //     title: "Absents Days",
        //     dataIndex: "absents",
        //     sorter: (a: any, b: any) => a.absents - b.absents,
        // },
        {
            title: "Holidays",
            dataIndex: "holidays",
            sorter: (a: any, b: any) => a.absents - b.absents,
        },

        {
            title: "Balance Leave",
            dataIndex: "available_leave_balance",
            sorter: (a: any, b: any) => a.available_leave_balance - b.available_leave_balance,
        },

        {
            title: "Leave Deduction",
            dataIndex: "leave_deduction",
            render: (text: any) => (
                <span style={{ color: "#E67E22", fontWeight: "bold" }}>
                    {formatCurrency(text, config)}
                </span>
            ),
            sorter: (a: any, b: any) => a.leave_deduction - b.leave_deduction,
        },
        {
            title: "Overtime Hours",
            dataIndex: "overtime_hours",
            sorter: (a: any, b: any) => a.overtime_hours - b.overtime_hours,
            render: (text: any) => (text !== undefined && text !== null) ? text : 0,
        },

        {
            title: "Earnings",
            dataIndex: "earnings",
            render: (text: any) => (
                <div>
                    {text && text.length > 0 ? (
                        text.map((earning: any) => (
                            <div key={earning.id}>
                                <span>
                                    {earning?.name || "None"}: {formatCurrency(earning?.amount || 0, config)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <span>₹0</span>
                    )}
                </div>
            ),
        },
        {
            title: "Deductions",
            dataIndex: "prorated_deduction_details",
            render: (text: any) => (
                <div>
                    {text && text.length > 0 ? (
                        text.map((deduction: any) => (
                            <div key={deduction.id}>
                                <span>
                                    {deduction?.name || "None"}: {formatCurrency(deduction?.proratedAmount || 0, config)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <span>₹0</span>
                    )}
                </div>
            ),
        },
        {
            title: "Overtime Pay",
            dataIndex: "overtime_pay",
            sorter: (a: any, b: any) => a.overtime_pay - b.overtime_pay,
            render: (text: any) => (
                <span style={{ color: "#E67E22", fontWeight: "bold" }}>
                    {formatCurrency(text, config)}
                </span>
            ),
        },
        {
            title: "Net Salary",
            dataIndex: "net_salary",
            sorter: (a: any, b: any) => a.net_salary - b.net_salary,
            render: (text: any) => (
                <span style={{ color: "#27AE60", fontWeight: "bold" }}>
                    {formatCurrency(text, config)}
                </span>
            ),
        },
    ];
    const transcationColumns = [
        {
            title: "Emp ID",
            dataIndex: "EmployeeNumber",
            sorter: (a: any, b: any) => a.EmployeeNumber.length - b.EmployeeNumber.length,
        },
        {
            title: "Name",
            dataIndex: "EmployeeName",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center file-name-icon">
                    <div className="avatar avatar-md">
                        <img
                            src={record.PictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                            alt="img"
                            className="img-fluid rounded-circle"
                        />
                    </div>
                    <div className="ms-2">
                        <h6 className="fw-medium">
                            <Link to="#">{record.EmployeeName}</Link>
                        </h6>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.EmployeeName.length - b.EmployeeName.length,
        },

        {
            title: "Status",
            dataIndex: "status",
            sorter: (a: any, b: any) => {
                const statusOrder = ['pending', 'processed'];
                return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
            },
            render: (status: string) => {
                let color = '';
                let text = '';

                if (status === 'Processed') {
                    color = 'green';
                    text = 'Processed';
                } else if (status === 'Pending') {
                    color = 'orange';
                    text = 'Pending';
                }

                return (
                    <span style={{ color: color }}>
                        {text}
                    </span>
                );
            }
        },

        {
            title: "Leaves Taken",
            dataIndex: "leaves_taken",
            sorter: (a: any, b: any) => a.leaves_taken - b.leaves_taken,
        },
        {
            title: "Balance Leave",
            dataIndex: "balance_leave",
            sorter: (a: any, b: any) => a.balance_leave - b.balance_leave,
        },
        {
            title: "Present",
            dataIndex: "total_attendance_count",
            sorter: (a: any, b: any) => a.total_attendance_count - b.total_attendance_count,
        },
        {
            title: "Leave Deduction",
            dataIndex: "leave_deduction",
            render: (text: any) => (
                <span style={{ color: "#E67E22", fontWeight: "bold" }}>
                    {formatCurrency(text, config)}
                </span>
            ),
            sorter: (a: any, b: any) => a.leave_deduction - b.leave_deduction,
        },
        {
            title: "Overtime Hours",
            dataIndex: "overtime_hours",
            sorter: (a: any, b: any) => a.overtime_hours - b.overtime_hours,
            render: (text: any) => (text !== undefined && text !== null) ? text : 0,
        },

        {
            title: "Earnings",
            dataIndex: "earnings_json",
            render: (text: any) => (
                <div>
                    {text && text.length > 0 ? (
                        text.map((earning: any) => (
                            <div key={earning.id}>
                                <span>
                                    {earning?.name || "None"}: {formatCurrency(earning?.amount || 0, config)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <span>₹0</span>
                    )}
                </div>
            ),
        },
        {
            title: "Deductions",
            dataIndex: "deductions_json",
            render: (text: any) => (
                <div>
                    {text && text.length > 0 ? (
                        text.map((deduction: any) => (
                            <div key={deduction.id}>
                                <span>
                                    {deduction?.name || "None"}: {formatCurrency(deduction?.proratedAmount || 0, config)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <span>₹0</span>
                    )}
                </div>
            ),
        },
        {
            title: "Overtime Pay",
            dataIndex: "overtime_pay",
            sorter: (a: any, b: any) => a.overtime_pay - b.overtime_pay,
            render: (text: any) => (
                <span style={{ color: "#E67E22", fontWeight: "bold" }}>
                    ₹{text?.toLocaleString()}
                </span>
            ),
        },
        {
            title: "Net Salary",
            dataIndex: "net_salary",
            sorter: (a: any, b: any) => a.net_salary - b.net_salary,
            render: (text: any) => (
                <span style={{ color: "#27AE60", fontWeight: "bold" }}>
                    ₹{text?.toLocaleString()}
                </span>
            ),
        },

        {
            title: "Action",
            dataIndex: "action",
            render: (text: string, record: any) => {
                const hasPayslip = payslipMap.has(record.transaction_id);

                return (
                    <div className="action-icon d-inline-flex">
                        {/* Generate Payslip - disable if already generated */}
                        <span
                            className={`badge bg-info d-flex align-items-center justify-content-center py-2 ${hasPayslip ? 'disabled' : ''}`}
                            style={{
                                cursor: hasPayslip ? 'not-allowed' : 'pointer',
                                opacity: hasPayslip ? 0.5 : 1,
                                pointerEvents: hasPayslip ? 'none' : 'auto'
                            }}
                            onClick={() => {
                                if (!hasPayslip) handleGeneratePayslip(record);
                            }}
                            title={hasPayslip ? "Payslip already generated" : "Generate Payslip"}
                        >
                            <i className="fas fa-file-invoice" />
                        </span>

                        {/* Only show other actions if payslip exists */}
                        {hasPayslip && (
                            <>
                                {/* Download */}
                                <span
                                    className="badge bg-success d-flex align-items-center justify-content-center py-2 ms-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => downloadPayslipAsPDF(record)}
                                    title="Download Payslip"
                                >
                                    <i className="fas fa-download" />
                                </span>

                                {/* View */}
                                <span
                                    className="badge bg-primary d-flex align-items-center justify-content-center py-2 ms-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setSelectedRowData(record);
                                        setIsPaySlipModalVisible(true);
                                    }}
                                    title="View Payslip"
                                >
                                    <i className="fas fa-eye" />
                                </span>

                                {/* Print */}
                                <span
                                    className="badge bg-warning d-flex align-items-center justify-content-center py-2 ms-2"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => printPayslip(record)}
                                    title="Print Payslip"
                                >
                                    <i className="fas fa-print" />
                                </span>
                            </>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">

                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Employee Salary</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">HR</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Employee Salary
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            {canExport && (
                                <div className="me-2 mb-2">
                                    <div className="dropdown">
                                        <Link
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            <i className="ti ti-file-export me-1" />
                                            Export
                                        </Link>
                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li>
                                                <button
                                                    onClick={() => exportToCSV(exportEmployeeSalary, 'salaryTransaction_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-pdf me-1" />
                                                    Export as CSV
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToExcel(exportEmployeeSalary, 'salaryTransaction_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as Excel{" "}
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToPDF(exportEmployeeSalary, 'salaryTransaction_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as PDF{" "}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex">
                                {/* Year Dropdown */}
                                <div className="mb-2 me-2">
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-primary dropdown-toggle d-flex align-items-center"
                                            type="button"
                                            id="dropdownYearButton"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            <i className="ti ti-calendar me-2" />
                                            {selectedYear}
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby="dropdownYearButton">
                                            {/* You can replace these years with dynamic data if needed */}
                                            {years?.map((year: any, index: any) => (
                                                <li key={index}>
                                                    <a className="dropdown-item" href="#" onClick={() => handleYearSelect(year)}>
                                                        {year}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Month Dropdown (only visible after selecting a year) */}
                                {selectedYear !== 'Select Year' && (
                                    <div className="mb-2 me-2">
                                        <div className="dropdown">
                                            <button
                                                className="btn btn-primary dropdown-toggle d-flex align-items-center"
                                                type="button"
                                                id="dropdownMonthButton"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <i className="ti ti-calendar me-2" />
                                                {selectedMonth}
                                            </button>
                                            <ul className="dropdown-menu" aria-labelledby="dropdownMonthButton">
                                                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                                                    <li key={index}>
                                                        <a className="dropdown-item" href="#" onClick={() => handleMonthSelect(month)}>
                                                            {month}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>


                            <div className="mb-2">
                                <button
                                    className="btn btn-success d-flex align-items-center"
                                    onClick={showModal}
                                >
                                    {/* <i className="fas fa-spinner fa-spin me-2" /> */}
                                    Process Salary
                                </button>
                            </div>

                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* <div className="row">
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-danger-transparent border border-danger d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-wallet fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Total Net Pay
                                                </p>
                                                <h4>₹123233.23</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-lock fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Salaries On Hold
                                                </p>
                                                <h4>₹00.0</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-credit-card fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Advance Pay
                                                </p>
                                                <h4>₹23343.00</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-2">
                                                <span className="p-2 br-10 bg-info-transparent border border-info d-flex align-items-center justify-content-center">
                                                    <i className="ti ti-gift fs-18" />
                                                </span>
                                            </div>
                                            <div>
                                                <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">
                                                    Bonus and Incentive
                                                </p>
                                                <h4>₹10222.00</h4>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> */}
                    {/* /Breadcrumb */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>Employee Salary</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown me-3">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) : "Select Status"}
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('Processed')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Processed
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('Pending')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Pending
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                                Descending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                                                Last Month
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                                                Last 7 Days
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKeyTransaction} dataSource={filteredData} columns={transcationColumns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>

            <Drawer
                title="Employee Salary Strucutre"
                placement="right"
                width={1000}
                onClose={handleCancel}
                visible={isModalVisible}
                style={{ marginTop: 50, paddingBottom: 50 }}
            >
                <form id="form-table">
                    <div className="d-flex justify-content-center mb-4 px-5">
                        <div className="card w-100 w-md-50 shadow-md border-primary">
                            <div className="card-body">
                                <h5 className="card-title text-center text-primary mb-4">Employee Salary Information</h5>
                                <div className="d-flex justify-content-between align-items-center" style={{ lineHeight: '1.5' }}>
                                    <span className="info-item mb-2 d-flex align-items-center">
                                        <strong className="fw-bold me-2" style={{ fontSize: '1rem' }}>Month</strong>
                                        <span className="badge bg-primary" style={{ fontSize: '0.7rem', padding: '0.5rem 0.8rem' }}>{selectedMonth} {selectedYear}</span>
                                    </span>
                                    <span className="info-item mb-2 d-flex align-items-center">
                                        <strong className="fw-bold me-2" style={{ fontSize: '1rem' }}>Total Days</strong>
                                        <span className="badge bg-success" style={{ fontSize: '0.7rem', padding: '0.5rem 0.8rem' }}>{salryCalculation[0]?.total_days_in_month}</span>
                                    </span>
                                    {/* <span className="info-item mb-2 d-flex align-items-center">
                                        <strong className="fw-bold me-2" style={{ fontSize: '1rem' }}>Weekly Off</strong>
                                        <span className="badge bg-warning" style={{ fontSize: '0.7rem', padding: '0.5rem 0.8rem' }}>{salryCalculation[0]?.weekend_count}</span>
                                    </span> */}
                                    {/* <span className="info-item d-flex align-items-center">
                                        <strong className="fw-bold me-2" style={{ fontSize: '1rem' }}>Holiday</strong>
                                        <span className="badge bg-info" style={{ fontSize: '0.7rem', padding: '0.5rem 0.8rem' }}>{salryCalculation[0]?.holiday_count}</span>
                                    </span> */}
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Table Component */}
                    <div className="table-container px-5 py-3 shadow-md card">
                        <Table
                            key={tableKey}
                            dataSource={salryCalculation}
                            columns={columns}
                        />
                    </div>

                    {/* Modal Footer */}
                    <div className="modal-footer">
                        <button type="submit" className="btn btn-primary btn-lg process-btn" onClick={handleProcessSalary}>
                            Process Salary
                        </button>
                    </div>
                </form>
            </Drawer >
            <PayslipModal isPaySlipModalVisible={isPaySlipModalVisible} setIsPaySlipModalVisible={setIsPaySlipModalVisible} selectedRowData={selectedRowData} downloadPayslipAsPDF={downloadPayslipAsPDF} />
        </>
    );
};

export default EmployeeSalary;
